/**
 * Gamification service for managing user progress and achievements
 * Handles XP tracking, task completion, and readiness calculations
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  arrayUnion,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import {
  UserGamification,
  GamificationTask,
  DEFAULT_TASKS,
  XP_TARGET,
  READINESS_LEVELS,
  ReadinessLevel,
} from '../../types/gamification';

const GAMIFICATION_COLLECTION = 'userGamification';
const COMPLETIONS_COLLECTION = 'taskCompletions';

/**
 * Initialize gamification data for a new user
 */
export const initializeUserGamification = async (
  userId: string
): Promise<UserGamification> => {
  const userGamificationRef = doc(db, GAMIFICATION_COLLECTION, userId);

  const initialData: Omit<UserGamification, 'createdAt' | 'updatedAt'> = {
    userId,
    totalXP: 0,
    readinessPercentage: 0,
    completedTasks: [],
  };

  await setDoc(userGamificationRef, {
    ...initialData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    ...initialData,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
};

/**
 * Get user's gamification data
 */
export const getUserGamification = async (
  userId: string
): Promise<UserGamification | null> => {
  const userGamificationRef = doc(db, GAMIFICATION_COLLECTION, userId);
  const docSnap = await getDoc(userGamificationRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    userId: data.userId,
    totalXP: data.totalXP,
    readinessPercentage: data.readinessPercentage,
    completedTasks: data.completedTasks || [],
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
};

/**
 * Award XP to a user for completing a task
 */
export const awardXP = async (
  userId: string,
  taskId: string,
  xpAmount: number
): Promise<void> => {
  const userGamificationRef = doc(db, GAMIFICATION_COLLECTION, userId);

  // Check if user gamification exists, if not create it
  const userGamification = await getUserGamification(userId);
  if (!userGamification) {
    await initializeUserGamification(userId);
  }

  // Update user's XP and completed tasks
  await updateDoc(userGamificationRef, {
    totalXP: increment(xpAmount),
    completedTasks: arrayUnion(taskId),
    updatedAt: serverTimestamp(),
  });

  // Recalculate readiness percentage
  const updatedGamification = await getUserGamification(userId);
  if (updatedGamification) {
    const newPercentage = calculateReadinessPercentage(
      updatedGamification.totalXP
    );
    await updateDoc(userGamificationRef, {
      readinessPercentage: newPercentage,
      updatedAt: serverTimestamp(),
    });
  }

  // Record task completion
  const completionRef = doc(collection(db, COMPLETIONS_COLLECTION));
  await setDoc(completionRef, {
    taskId,
    userId,
    completedAt: serverTimestamp(),
    xpAwarded: xpAmount,
  });
};

/**
 * Calculate readiness percentage based on total XP
 */
export const calculateReadinessPercentage = (totalXP: number): number => {
  const percentage = Math.min((totalXP / XP_TARGET) * 100, 100);
  return Math.round(percentage * 10) / 10; // Round to 1 decimal place
};

/**
 * Get the current readiness level based on percentage
 */
export const getReadinessLevel = (
  percentage: number
): ReadinessLevel => {
  return (
    READINESS_LEVELS.find(
      (level) => percentage >= level.min && percentage <= level.max
    ) || READINESS_LEVELS[0]
  );
};

/**
 * Check if a task is completed by the user
 */
export const isTaskCompleted = async (
  userId: string,
  taskId: string
): Promise<boolean> => {
  const userGamification = await getUserGamification(userId);
  return userGamification?.completedTasks.includes(taskId) || false;
};

/**
 * Get all available tasks
 */
export const getAllTasks = (): GamificationTask[] => {
  return DEFAULT_TASKS;
};

/**
 * Get next recommended task for the user
 */
export const getNextTask = async (
  userId: string
): Promise<GamificationTask | null> => {
  const userGamification = await getUserGamification(userId);
  if (!userGamification) {
    return DEFAULT_TASKS[0];
  }

  const incompleteTasks = DEFAULT_TASKS.filter(
    (task) => !userGamification.completedTasks.includes(task.id)
  );

  return incompleteTasks.length > 0 ? incompleteTasks[0] : null;
};

/**
 * Get the latest completed task for a user
 */
export const getLatestCompletedTask = async (
  userId: string
): Promise<{ task: GamificationTask; completedAt: Date; xpAwarded: number } | null> => {
  try {
    const completionsRef = collection(db, COMPLETIONS_COLLECTION);
    const q = query(
      completionsRef,
      where('userId', '==', userId),
      orderBy('completedAt', 'desc'),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }

    const completionDoc = querySnapshot.docs[0];
    const completionData = completionDoc.data();
    
    // Find the task details
    const task = DEFAULT_TASKS.find(t => t.id === completionData.taskId);
    
    if (!task) {
      return null;
    }

    return {
      task,
      completedAt: completionData.completedAt?.toDate() || new Date(),
      xpAwarded: completionData.xpAwarded || task.xpReward,
    };
  } catch (error) {
    console.error('Error fetching latest completed task:', error);
    return null;
  }
};

/**
 * Get user's XP earned per day for the current ISO week (Mon–Sun).
 * Returns a 7-element array [Mon, Tue, Wed, Thu, Fri, Sat, Sun], each value
 * being the sum of xpAwarded from taskCompletions that day.
 */
export const getWeeklyXP = async (userId: string): Promise<number[]> => {
  try {
    const now = new Date();
    // Calculate Monday 00:00:00 of the current week
    const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon … 6=Sat
    const daysFromMonday = (dayOfWeek + 6) % 7;
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - daysFromMonday);

    // Sunday 23:59:59.999 (exclusive upper bound = next Monday 00:00)
    const nextMonday = new Date(monday);
    nextMonday.setDate(nextMonday.getDate() + 7);

    const completionsRef = collection(db, COMPLETIONS_COLLECTION);
    const q = query(
      completionsRef,
      where('userId', '==', userId),
      where('completedAt', '>=', Timestamp.fromDate(monday)),
      where('completedAt', '<', Timestamp.fromDate(nextMonday))
    );

    const snapshot = await getDocs(q);
    const daily = [0, 0, 0, 0, 0, 0, 0]; // index 0=Mon … 6=Sun

    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      const completedAt = data.completedAt?.toDate?.() as Date | undefined;
      if (!completedAt) return;
      const dayIdx = (completedAt.getDay() + 6) % 7; // convert Sun=0 → Mon=0
      daily[dayIdx] += (data.xpAwarded as number) || 0;
    });

    return daily;
  } catch {
    return [0, 0, 0, 0, 0, 0, 0];
  }
};

/**
 * Get user's progress summary
 */
export const getUserProgressSummary = async (userId: string) => {
  const userGamification = await getUserGamification(userId);
  if (!userGamification) {
    return {
      totalXP: 0,
      readinessPercentage: 0,
      completedTasks: [],
      totalTasks: DEFAULT_TASKS.length,
      level: READINESS_LEVELS[0],
      nextTask: DEFAULT_TASKS[0],
    };
  }

  const level = getReadinessLevel(userGamification.readinessPercentage);
  const nextTask = await getNextTask(userId);

  return {
    totalXP: userGamification.totalXP,
    readinessPercentage: userGamification.readinessPercentage,
    completedTasks: userGamification.completedTasks,
    totalTasks: DEFAULT_TASKS.length,
    level,
    nextTask,
  };
};
